/**
 * Submission Analytics Service
 * Handles field-level aggregation, filtering, and form summary analytics
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { SUBMISSION_ANALYTICS_ENDPOINTS } from '../endpoints';

/**
 * @typedef {Object} FieldFilterDto
 * @property {string} fieldKey - Field key to filter on
 * @property {string} operator - Filter operator (equals, gt, gte, lt, lte, between, contains, startsWith, in, before, after, isNull, isNotNull)
 * @property {string} [value] - Primary filter value
 * @property {string} [valueTo] - Upper bound for between operator
 * @property {string[]} [values] - Values array for in operator
 */

/**
 * @typedef {Object} AggregationRequest
 * @property {string} [fromDate] - Start date filter (ISO 8601)
 * @property {string} [toDate] - End date filter (ISO 8601)
 * @property {string} [status] - Submission status filter
 * @property {boolean} [includeInactive] - Include soft-deleted submissions
 * @property {FieldFilterDto[]} [filters] - Field-level filters for filtered aggregation
 * @property {string[]} [fieldKeys] - Limit aggregation to specific fields
 */

/**
 * @typedef {Object} FilterRequest
 * @property {FieldFilterDto[]} filters - Field filter conditions (ANDed)
 * @property {string} [fromDate] - Start date filter
 * @property {string} [toDate] - End date filter
 * @property {string} [status] - Status filter
 * @property {boolean} [includeInactive] - Include inactive submissions
 * @property {number} [page] - Page number (default 1)
 * @property {number} [pageSize] - Page size (default 20, max 100)
 */

const submissionAnalyticsService = {
  /**
   * Get aggregation statistics for a form's submissions.
   * Returns per-numeric-field stats (count, min, max, mean, sum, stddev, median)
   * and per-string-field value distributions.
   * @param {number} formSchemaId - Form schema ID
   * @param {AggregationRequest} [request={}] - Aggregation options and optional filters
   * @returns {Promise<Object>} AggregationResultDto
   */
  getAggregation: async (formSchemaId, request = {}) => {
    return apiRequest(() =>
      axiosInstance.post(SUBMISSION_ANALYTICS_ENDPOINTS.AGGREGATE(formSchemaId), request)
    );
  },

  /**
   * Get structural summary of a form's submissions.
   * Returns submission count, date range, status breakdown, and per-field completeness.
   * @param {number} formSchemaId - Form schema ID
   * @returns {Promise<Object>} FormSummaryResultDto
   */
  getSummary: async (formSchemaId) => {
    return apiRequest(() =>
      axiosInstance.get(SUBMISSION_ANALYTICS_ENDPOINTS.SUMMARY(formSchemaId))
    );
  },

  /**
   * Filter submissions by field-level conditions.
   * Returns paginated FormSubmissionResponseDto[] matching all filter conditions.
   * @param {number} formSchemaId - Form schema ID
   * @param {FilterRequest} request - Filter conditions and pagination
   * @returns {Promise<Object>} PagedResult<FormSubmissionResponseDto>
   */
  filterSubmissions: async (formSchemaId, request = {}) => {
    return apiRequest(() =>
      axiosInstance.post(SUBMISSION_ANALYTICS_ENDPOINTS.FILTER(formSchemaId), request)
    );
  },

  /**
   * Group submissions by a categorical field and aggregate a numeric field per group.
   * Powers the Chart Builder tab in the analytics panel.
   * @param {number} formSchemaId - Form schema ID
   * @param {Object} request - GroupBy request (groupByFieldKey, aggregateFieldKey, function, filters)
   * @returns {Promise<Object>} GroupByResultDto
   */
  getGroupBy: async (formSchemaId, request = {}) => {
    return apiRequest(() =>
      axiosInstance.post(SUBMISSION_ANALYTICS_ENDPOINTS.GROUPBY(formSchemaId), request)
    );
  },

  /**
   * Get scatter plot data: pairs of (x, y) numeric field values per submission.
   * @param {number} formSchemaId
   * @param {Object} request - { xFieldKey, yFieldKey, maxPoints, filters... }
   * @returns {Promise<Object>} ScatterResultDto
   */
  getScatter: async (formSchemaId, request = {}) => {
    return apiRequest(() =>
      axiosInstance.post(SUBMISSION_ANALYTICS_ENDPOINTS.SCATTER(formSchemaId), request)
    );
  },
};

export default submissionAnalyticsService;
